import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

import { FdkIconComponent } from '../icon/icon.component';

let nextSelectId = 0;

export interface FdkSelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export type FdkSelectOptionInput = string | FdkSelectOption;

@Component({
  selector: 'fdk-select',
  imports: [FdkIconComponent],
  templateUrl: './select.component.html',
  styleUrl: './select.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FdkSelectComponent),
      multi: true,
    },
  ],
})
export class FdkSelectComponent implements ControlValueAccessor {
  @Input({ required: true }) label!: string;
  @Input({ required: true }) options: readonly FdkSelectOptionInput[] = [];
  @Input() placeholder: string = '';
  @Input() value: string = '';
  @Output() readonly valueChange = new EventEmitter<string>();
  @Input() name: string | null = null;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input({ transform: booleanAttribute }) required = false;
  @Input({ transform: booleanAttribute }) error = false;
  @Input() errorMessage = '';

  readonly selectId = `fdk-select-${nextSelectId++}`;
  readonly errorMessageId = `${this.selectId}-error`;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  get showErrorMessage(): boolean {
    return this.error && this.errorMessage.trim().length > 0;
  }

  get describedBy(): string | null {
    return this.showErrorMessage ? this.errorMessageId : null;
  }

  optionLabel(option: FdkSelectOptionInput): string {
    return typeof option === 'string' ? option : option.label;
  }

  optionValue(option: FdkSelectOptionInput): string {
    return typeof option === 'string' ? option : option.value;
  }

  optionDisabled(option: FdkSelectOptionInput): boolean {
    return typeof option === 'string' ? false : !!option.disabled;
  }

  handleChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.value = value;
    this.valueChange.emit(value);
    this.onChange(value);
  }

  handleBlur(): void {
    this.onTouched();
  }

  writeValue(value: string | null | undefined): void {
    this.value = value ?? '';
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(onChange: (value: string) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.changeDetectorRef.markForCheck();
  }
}
